import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, GripVertical, Clapperboard, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import api, { formatApiError, imageUrl } from "../../lib/api";

const fmt = (s) => {
  const m = Math.floor((s || 0) / 60);
  const ss = Math.round((s || 0) % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
};

export default function Videos() {
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [dragCat, setDragCat] = useState(null);
  const [dragVideo, setDragVideo] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const fetchAll = () => {
    Promise.all([api.get("/admin/video-categories"), api.get("/admin/videos")])
      .then(([c, v]) => {
        setCategories(c.data);
        setVideos(v.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "Kategorisiz";

  const addCategory = async () => {
    if (addingCat || !newCat.trim()) return;
    setAddingCat(true);
    try {
      await api.post("/admin/video-categories", { name: newCat.trim() });
      toast.success("Kategori eklendi");
      setNewCat("");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingCat(false);
    }
  };

  const onDropCat = async (targetIndex) => {
    if (dragCat === null || dragCat === targetIndex) {
      setDragCat(null);
      return;
    }
    const reordered = [...categories];
    const [moved] = reordered.splice(dragCat, 1);
    reordered.splice(targetIndex, 0, moved);
    setCategories(reordered);
    setDragCat(null);
    try {
      await api.post("/admin/video-categories/reorder", { ids: reordered.map((c) => c.id) });
      toast.success("Kategori sıralaması güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
      fetchAll();
    }
  };

  const onDropVideo = async (targetIndex) => {
    if (dragVideo === null || dragVideo === targetIndex) {
      setDragVideo(null);
      return;
    }
    const reordered = [...videos];
    const [moved] = reordered.splice(dragVideo, 1);
    reordered.splice(targetIndex, 0, moved);
    setVideos(reordered);
    setDragVideo(null);
    try {
      await api.post("/admin/videos/reorder", { ids: reordered.map((v) => v.id) });
      toast.success("Video sıralaması güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
      fetchAll();
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      setDuration(Math.round(v.duration));
      URL.revokeObjectURL(url);
    };
    v.src = url;
  };

  const upload = async () => {
    if (uploading) return;
    if (!file) {
      toast.error("Video dosyası seçmelisiniz");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("category_id", categoryId);
      fd.append("duration", String(duration));
      await api.post("/admin/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      toast.success("Video yüklendi");
      setFile(null);
      setTitle("");
      setCategoryId("");
      setDuration(0);
      if (fileRef.current) fileRef.current.value = "";
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const changeVideoCategory = async (id, cid) => {
    try {
      await api.patch(`/admin/videos/${id}`, { category_id: cid });
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, category_id: cid } : v)));
      toast.success("Kategori güncellendi");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {    try {
      if (deleteTarget.type === "cat") {
        await api.delete(`/admin/video-categories/${deleteTarget.id}`);
        toast.success("Kategori silindi (videolar kategorisiz kaldı)");
      } else {
        await api.delete(`/admin/videos/${deleteTarget.id}`);
        toast.success("Video silindi");
      }
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteTarget(null);
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="admin-videos-page">
      <div className="mb-1">
        <h1 className="font-display font-700 text-xl text-white">Video Arşiv Yönetimi</h1>
      </div>
      <p className="text-sm text-[#64748B] mb-8">Kategorileri oluşturun ve sürükleyerek sıralayın, videoları yükleyin. Sıralama üye paneline doğrudan yansır.</p>

      <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6">
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5" data-testid="video-upload-card">
            <h3 className="text-xs font-bold tracking-wide text-cyan-300 mb-4 flex items-center gap-1.5"><Upload size={13} /> VİDEO YÜKLE</h3>
            <div className="space-y-4">
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="video-file-button"
                className="w-full rounded-xl border border-dashed border-white/15 bg-black/40 py-8 flex flex-col items-center gap-2 hover:border-cyan-400/50 transition-colors">
                <Clapperboard size={22} className="text-[#64748B]" />
                <span className="text-xs text-[#A0ABC0]">{file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB, ${fmt(duration)})` : "MP4, WebM veya MOV seç (maks 100MB)"}</span>
              </button>
              <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => pickFile(e.target.files[0])} data-testid="video-file-input" />
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Video Başlığı</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Örn: Racon Takım Intro" data-testid="video-title-input" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Kategori</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="video-category-select" className={`${inputCls} cursor-pointer`}>
                  <option value="">Kategorisiz</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {uploading && (
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="video-upload-progress">
                  <div className="h-full bg-gradient-to-r from-[#0047FF] to-[#00F0FF] transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
              <button onClick={upload} disabled={uploading} data-testid="video-upload-submit"
                className="btn-glow w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-white py-3 rounded-full disabled:opacity-50">
                <Upload size={14} /> {uploading ? `Yükleniyor... %${progress}` : "Videoyu Yükle"}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5" data-testid="video-categories-card">
            <h3 className="text-xs font-bold tracking-wide text-cyan-300 mb-4 flex items-center gap-1.5"><FolderPlus size={13} /> KATEGORİLER</h3>
            <div className="flex gap-2 mb-4">
              <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()}
                placeholder="Örn: Racon Takım" data-testid="category-name-input" className={inputCls} />
              <button onClick={addCategory} disabled={addingCat} data-testid="category-add-button"
                className="btn-glow inline-flex items-center gap-1.5 text-xs font-semibold text-white px-4 rounded-full shrink-0 disabled:opacity-50">
                <Plus size={14} /> Ekle
              </button>
            </div>
            <div className="space-y-2" data-testid="category-list">
              {categories.length === 0 && <p className="text-[11px] text-[#64748B] py-3 text-center">Henüz kategori yok</p>}
              {categories.map((c, index) => (
                <div key={c.id} data-testid={`category-row-${c.id}`}
                  draggable
                  onDragStart={() => setDragCat(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropCat(index)}
                  onDragEnd={() => setDragCat(null)}
                  className={`flex items-center gap-2.5 bg-black/30 border rounded-xl px-3 py-2.5 cursor-grab active:cursor-grabbing transition-colors ${
                    dragCat === index ? "opacity-40 border-cyan-400/50" : "border-white/[0.06] hover:border-cyan-400/30"
                  }`}>
                  <GripVertical size={14} className="text-[#64748B] shrink-0" data-testid={`category-drag-${c.id}`} />
                  <span className="text-xs font-semibold text-white flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] text-[#64748B] shrink-0">{c.video_count} video</span>
                  <button onClick={() => setDeleteTarget({ type: "cat", id: c.id, label: c.name })} data-testid={`category-delete-${c.id}`} aria-label="Sil"
                    className="text-[#64748B] hover:text-red-300 transition-colors shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5" data-testid="video-list-card">
          <h3 className="text-xs font-bold tracking-wide text-cyan-300 mb-4">VİDEOLAR ({videos.length})</h3>
          {loading ? (
            <p className="text-sm text-[#64748B]">Yükleniyor...</p>
          ) : videos.length === 0 ? (
            <div data-testid="videos-empty" className="rounded-xl border border-dashed border-white/10 py-16 text-center">
              <Clapperboard size={28} strokeWidth={1.5} className="text-[#64748B] mx-auto mb-3" />
              <p className="text-[#A0ABC0] text-sm">Henüz video yüklenmemiş.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="video-list">
              {videos.map((v, index) => (
                <div key={v.id} data-testid={`video-row-${v.id}`}
                  draggable
                  onDragStart={() => setDragVideo(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropVideo(index)}
                  onDragEnd={() => setDragVideo(null)}
                  className={`flex items-center gap-3 bg-black/30 border rounded-xl p-2.5 cursor-grab active:cursor-grabbing transition-colors ${
                    dragVideo === index ? "opacity-40 border-cyan-400/50" : "border-white/[0.06] hover:border-cyan-400/30"
                  }`}>
                  <GripVertical size={14} className="text-[#64748B] shrink-0" data-testid={`video-drag-${v.id}`} />
                  <div className="w-10 h-14 rounded-lg bg-[#0F1626] overflow-hidden shrink-0">
                    <video src={imageUrl(v.url)} className="w-full h-full object-cover" preload="metadata" muted playsInline />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{v.title || "İsimsiz Video"}</p>
                    <p className="text-[10px] text-[#64748B]">{fmt(v.duration)} · {((v.size || 0) / 1048576).toFixed(2)} MB</p>
                  </div>
                  <select
                    value={v.category_id}
                    onChange={(e) => changeVideoCategory(v.id, e.target.value)}
                    data-testid={`video-cat-select-${v.id}`}
                    aria-label="Kategori değiştir"
                    className="bg-black/40 border border-white/10 rounded-lg text-[11px] text-[#A0ABC0] px-2.5 py-1.5 outline-none cursor-pointer hover:border-cyan-400/40 transition-colors shrink-0 max-w-[130px]"
                  >
                    <option value="">Kategorisiz</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button onClick={() => setDeleteTarget({ type: "video", id: v.id, label: v.title || "İsimsiz Video" })} data-testid={`video-delete-${v.id}`} aria-label="Sil"
                    className="text-[#64748B] hover:text-red-300 transition-colors shrink-0 px-2">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="video-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTarget?.type === "cat" ? "Kategoriyi Sil" : "Videoyu Sil"}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">
              {deleteTarget?.type === "cat"
                ? `"${deleteTarget?.label}" kategorisi silinecek, içindeki videolar kategorisiz kalır. Emin misiniz?`
                : `"${deleteTarget?.label}" videosu kalıcı olarak silinecek. Emin misiniz?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="video-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="video-delete-confirm" onClick={doDelete} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
